import { Badge, LinearProgress, Tab, Tabs } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { PRESCRIPTION_BUILDER_VIEW_TYPES } from 'src/consumer/constants'
import { setPrescriptionViewSettingsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { isCommonResponseSuccessful, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithoutParams, postMPDataWithParams } from 'src/mentor/services'
import { getSPDataWithParams, postSPDataWithParams } from 'src/supportPortal/services/services'
import PrescriptionEditorSettings from './editorSettings/PrescriptionEditorSettings'
import PrescriptionPrintSettings from './printSettings/PrescriptionPrintSettings'

export const PRESCRIPTION_VIEW_SETTINGS_TYPE = {
  editorSettings: 'editorSettings',
  printSettings: 'printSettings',
}

export const PRESCRIPTION_VIEW_SETTINGS_TYPE_ARRAY = [
  {
    id: PRESCRIPTION_VIEW_SETTINGS_TYPE.editorSettings,
    title: 'Editor Settings',
  },
  {
    id: PRESCRIPTION_VIEW_SETTINGS_TYPE.printSettings,
    title: 'Print Settings',
  },
]

export default function PrescriptionViewSettings({
  settingsType,
  mentorProfileData,
  prescriptionViewSettingsGetApiSignature = '/api/secure/practitioner/emr-settings/settings/final-report-view',
  prescriptionViewSettingsSaveApiSignature = '/api/secure/practitioner/emr-settings/settings/final-report-view/save',
}) {
  const dispatch = useDispatch()
  const [prescriptionViewSettings, setPrescriptionViewSettings] = useState({
    prescriptionBuilderViewType: PRESCRIPTION_BUILDER_VIEW_TYPES.minimal,
    preferredPrescriptionLanguages: ['en'],
  })
  const [loading, setLoading] = useState({
    prescriptionViewSettings: true,
    saveLoading: false,
  })
  const [emrReportTypes, setEmrReportTypes] = useState([])
  const [activeTab, setActiveTab] = useState(false)

  const getPrescriptionViewSettings = useCallback(async () => {
    if (!activeTab) return

    setLoading((prev) => ({ ...prev, prescriptionViewSettings: true }))
    
    // Check if this is a support portal API
    const isSupportPortalApi = prescriptionViewSettingsGetApiSignature.includes('/api/secure/cc/support/')
    
    let resp
    if (isSupportPortalApi) {
      // Parse query params from URL if present
      const urlParts = prescriptionViewSettingsGetApiSignature.split('?')
      const pathname = urlParts[0]
      const params = {}
      
      // Extract query params if present
      if (urlParts.length > 1) {
        const queryString = urlParts[1]
        const searchParams = new URLSearchParams(queryString)
        searchParams.forEach((value, key) => {
          params[key] = value
        })
      }
      
      // Add emr-based-report-type
      params['emr-based-report-type'] = activeTab
      
      resp = await getSPDataWithParams(pathname, params)
      
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const data = resp?.data?.data || resp?.data || {}
        const fetchedSettings = data?.prescriptionViewSettings || data
        if (fetchedSettings) {
          setPrescriptionViewSettings(fetchedSettings)
          // Also update Redux store when fetching settings
          dispatch(setPrescriptionViewSettingsData(fetchedSettings))
        }
      }
    } else {
      // Mentor portal API
      const separator = prescriptionViewSettingsGetApiSignature.includes('?') ? '&' : '?'
      const apiUrl = `${prescriptionViewSettingsGetApiSignature}${separator}emr-based-report-type=${activeTab}`
      resp = await getMPDataWithoutParams(apiUrl)
      if (resp?.status === 200) {
        if (
          // isCommonResponseSuccessful(resp?.data?.code) &&
          !!resp?.data?.data?.prescriptionViewSettings
        ) {
          const fetchedSettings = resp?.data?.data?.prescriptionViewSettings
          setPrescriptionViewSettings(fetchedSettings)
          // Also update Redux store when fetching settings
          dispatch(setPrescriptionViewSettingsData(fetchedSettings))
        }
      }
    }
    setLoading((prev) => ({ ...prev, prescriptionViewSettings: false }))
  }, [activeTab, prescriptionViewSettingsGetApiSignature, dispatch])

  async function updatePrescriptionViewSettings() {
    if (!activeTab) return

    setLoading((prev) => ({ ...prev, saveLoading: true }))
    
    // Check if this is a support portal API
    const isSupportPortalApi = prescriptionViewSettingsSaveApiSignature.includes('/api/secure/cc/support/')
    
    let resp
    if (isSupportPortalApi) {
      // Parse query params from URL if present
      const urlParts = prescriptionViewSettingsSaveApiSignature.split('?')
      const pathname = urlParts[0]
      const params = {}
      
      // Extract query params if present
      if (urlParts.length > 1) {
        const queryString = urlParts[1]
        const searchParams = new URLSearchParams(queryString)
        searchParams.forEach((value, key) => {
          params[key] = value
        })
      }
      
      // Add emr-based-report-type
      params['emr-based-report-type'] = activeTab
      
      resp = await postSPDataWithParams(pathname, prescriptionViewSettings, params)
      
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        toast.success('Prescription view settings updated successfully')
        const data = resp?.data?.data || resp?.data || {}
        const updatedSettings = data?.prescriptionViewSettings || data
        
        setPrescriptionViewSettings(updatedSettings)
        // Update Redux store so PrescriptionMakingPage can access the updated settings
        dispatch(setPrescriptionViewSettingsData(updatedSettings))
      }
    } else {
      // Mentor portal API
      const separator = prescriptionViewSettingsSaveApiSignature.includes('?') ? '&' : '?'
      const apiUrl = `${prescriptionViewSettingsSaveApiSignature}${separator}emr-based-report-type=${activeTab}`
      
      resp = await postMPDataWithParams(apiUrl, prescriptionViewSettings)
      if (resp.status === 200) {
        if (isCommonResponseSuccessful(resp?.data?.data)) {
          toast.success('Prescription view settings updated successfully')
          const updatedSettings = resp?.data?.data?.prescriptionViewSettings
          
          setPrescriptionViewSettings(updatedSettings)
          // Update Redux store so PrescriptionMakingPage can access the updated settings
          dispatch(setPrescriptionViewSettingsData(updatedSettings))
        }
      }
    }
    // update prescription view settings
    setLoading((prev) => ({ ...prev, saveLoading: false }))
  }

  const FormComponent = useMemo(() => {
    switch (settingsType) {
      case PRESCRIPTION_VIEW_SETTINGS_TYPE.printSettings:
        return PrescriptionPrintSettings
      default:
        return PrescriptionEditorSettings
    }
  }, [settingsType])

  // Fetch EMR report types on mount
  const getEmrReportTypes = useCallback(async () => {
    try {
      const resp = await getMPDataWithoutParams('/api/health/emr-report-types')
      if (resp?.status === 200) {
        setEmrReportTypes(resp?.data || [])
        // Set first report type as active tab
        if (resp?.data?.length > 0) {
          setActiveTab(resp.data[0].id)
        }
      }
    } catch (error) {
      console.error('Failed to fetch EMR report types:', error)
      setEmrReportTypes([])
    }
  }, [])

  const reportTypesToReturn = useMemo(() => {
    let allowedReportTypes = (
      Object.entries(mentorProfileData?.teacherUnEditableFields?.servicesApproved || {}).map(
        ([key, approvedServices]) => approvedServices?.emrBasedReportTypes,
      ) || []
    ).flat()
    allowedReportTypes = [...new Set(allowedReportTypes)]
    return allowedReportTypes.map((reportTypeId, idx) => {
      let foundMainTitleBasedOnReportType =
        emrReportTypes?.find((type) => type?.id === reportTypeId)?.title || ''
      // const specTitles = approvedServices?.specializations?.map((specId) => {
      //   const spec = categories?.find((cat) => cat?.id === specId)
      //   return spec?.title || ''
      // })
      // let specsTitle = specTitles?.join(', ') || ''
      // if (!!specsTitle) {
      //   specsTitle = `(${specsTitle})`
      // }

      return {
        id: reportTypeId,
        reportType: reportTypeId,
        title: foundMainTitleBasedOnReportType,
      }
    })
  }, [emrReportTypes, mentorProfileData])

  console.log({ reportTypesToReturn, mentorProfileData, emrReportTypes })

  useEffect(() => {
    getEmrReportTypes()
  }, [getEmrReportTypes])

  // Fetch settings when activeTab changes
  useEffect(() => {
    if (activeTab) {
      getPrescriptionViewSettings()
    }
  }, [activeTab, getPrescriptionViewSettings])

  return (
    <div style={{ width: '100%', paddingBottom: '80px' }}>
      {loading.prescriptionViewSettings && <LinearProgress />}

      {reportTypesToReturn.length > 0 && activeTab && (
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {reportTypesToReturn.map((reportType) => (
            <Tab key={reportType.id} value={reportType.id} label={reportType.title} />
          ))}
        </Tabs>
      )}

      {!loading.prescriptionViewSettings && activeTab && (
        <>
          <FormComponent
            prescriptionViewSettings={prescriptionViewSettings}
            setPrescriptionViewSettings={setPrescriptionViewSettings}
            updatePrescriptionViewSettings={updatePrescriptionViewSettings}
            saveLoading={loading.saveLoading}
          />
        </>
      )}
    </div>
  )
}
